export const SLAB_STYLE_CATEGORIES = [
  { id: 'heritage-georgian', label: 'Heritage / Georgian' },
  { id: 'country-cottage', label: 'Country / Cottage' },
  { id: 'classic', label: 'Classic' },
  { id: 'contemporary', label: 'Contemporary' },
] as const;

export type SlabStyleCategoryId = (typeof SLAB_STYLE_CATEGORIES)[number]['id'];

export interface ExternalSlabArtworkFile {
  categoryId: SlabStyleCategoryId;
  fileName: string;
}

export interface ExternalSlabStyle {
  id: string;
  name: string;
  customerLabel: string;
  categoryId: SlabStyleCategoryId;
  categoryLabel: string;
  publicPath: string;
}

const CATEGORY_BY_ID = Object.fromEntries(
  SLAB_STYLE_CATEGORIES.map((category) => [category.id, category])
) as Record<SlabStyleCategoryId, (typeof SLAB_STYLE_CATEGORIES)[number]>;

function artworkFilesFromBuild(): ExternalSlabArtworkFile[] {
  const files = globalThis.__DOOR_SLAB_ARTWORK_FILES__;
  return Array.isArray(files) ? files : [];
}

export function slabStyleIdFromFileName(fileName: string): string {
  return fileName.replace(/\.svg$/i, '').toLowerCase();
}

function slabNameStemFromFileName(fileName: string): string {
  const id = slabStyleIdFromFileName(fileName);
  return id.startsWith('the-') ? id.slice(4) : id;
}

export function displayNameFromSlabFileName(fileName: string): string {
  return slabNameStemFromFileName(fileName)
    .split(/[-_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function customerLabelFromSlabFileName(fileName: string): string {
  return `The ${displayNameFromSlabFileName(fileName)}`;
}

export function styleFromArtworkFile(file: ExternalSlabArtworkFile): ExternalSlabStyle {
  const category = CATEGORY_BY_ID[file.categoryId];
  const name = displayNameFromSlabFileName(file.fileName);
  return {
    id: slabStyleIdFromFileName(file.fileName),
    name,
    customerLabel: customerLabelFromSlabFileName(file.fileName),
    categoryId: category.id,
    categoryLabel: category.label,
    publicPath: `/images/door-designer/door-slabs/${category.id}/${file.fileName}`,
  };
}

export function buildExternalSlabStyles(
  files: ExternalSlabArtworkFile[] = artworkFilesFromBuild()
): ExternalSlabStyle[] {
  return files.map(styleFromArtworkFile);
}

/**
 * Typed catalogue built from SVG files under
 * `public/images/door-designer/door-slabs/{category}/`.
 * Product names are not hand-maintained.
 */
export const EXTERNAL_SLAB_STYLES: ExternalSlabStyle[] = buildExternalSlabStyles();

export const DEFAULT_EXTERNAL_SLAB_STYLE_ID =
  EXTERNAL_SLAB_STYLES.find((entry) => entry.id === 'bosham')?.id ??
  EXTERNAL_SLAB_STYLES[0]?.id ??
  'placeholder-solid';

export function getExternalSlabStyle(styleId: string): ExternalSlabStyle | undefined {
  return EXTERNAL_SLAB_STYLES.find((entry) => entry.id === styleId);
}

export function customerLabelForSlabStyle(styleId: string): string {
  if (styleId === 'placeholder-solid') return 'Solid';
  const match = getExternalSlabStyle(styleId);
  if (match) return match.customerLabel;
  return customerLabelFromSlabFileName(`${styleId}.svg`);
}

export function slabStylesByCategory(): Array<{
  categoryId: SlabStyleCategoryId;
  categoryLabel: string;
  styles: ExternalSlabStyle[];
}> {
  return SLAB_STYLE_CATEGORIES.map((category) => ({
    categoryId: category.id,
    categoryLabel: category.label,
    styles: EXTERNAL_SLAB_STYLES.filter((entry) => entry.categoryId === category.id),
  })).filter((group) => group.styles.length > 0);
}

declare global {
  // Injected at webpack compile time and in Jest setup from the public slab folders.
  // eslint-disable-next-line no-var
  var __DOOR_SLAB_ARTWORK_FILES__: ExternalSlabArtworkFile[] | undefined;
}
